import React, { useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Space,
  Form,
  Input,
  DatePicker,
  InputNumber,
  Button,
  Tag,
  Modal,
  Avatar,
  Divider,
  Select,
  Empty,
} from "antd";
import dayjs from "dayjs";
import apiPath from "../../constants/apiPath";
import useRequest from "../../hooks/useRequest";
import { Severty, ShowToast } from "../../helper/toast";

const { Title, Text } = Typography;

const formatDuration = (mins) => {
  if (!mins && mins !== 0) return "-";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};

const initials = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const i1 = parts[0]?.[0] || "A";
  const i2 = parts[1]?.[0] || parts[0]?.[1] || "";
  return (i1 + i2).toUpperCase();
};

const AgentBooking = () => {
  const { request } = useRequest();
  const [form] = Form.useForm();
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [passengers, setPassengers] = useState([{ name: "", age: null, gender: "M" }]);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [sortBy, setSortBy] = useState("price");

  const onSearch = (values) => {
    setSearching(true);
    const payload = {
      source: values.source?.toUpperCase(),
      destination: values.destination?.toUpperCase(),
      date: values.date?.format("YYYY-MM-DD"),
      passengers: values.passengers || 1,
      sort_by: sortBy,
      filters: {},
    };

    request({
      url: `${apiPath.baseURL}/${apiPath.agentSearch}`,
      method: "POST",
      data: payload,
      onSuccess: (res) => {
        const data = res?.data || res;
        setResults(data || []);
      },
      onError: () => {
        ShowToast("Failed to search flights", Severty.ERROR);
      },
      finally: () => setSearching(false),
    });
  };

  const openBooking = (result) => {
    const pax = form.getFieldValue("passengers") || 1;
    setSelectedResult(result);
    setPassengers(
      Array.from({ length: pax }).map(() => ({
        name: "",
        age: null,
        gender: "M",
      })),
    );
    setBookingModalOpen(true);
  };

  const updatePassenger = (idx, field, value) => {
    setPassengers((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const submitBooking = () => {
    if (!selectedResult) return;
    const flightInstanceId = selectedResult.flight_instance_id;
    const cleanPassengers = passengers.filter((p) => p.name);
    if (!cleanPassengers.length) {
      ShowToast("Please enter at least one passenger", Severty.ERROR);
      return;
    }

    setBookingLoading(true);
    request({
      url: `${apiPath.baseURL}/${apiPath.agentBookings}`,
      method: "POST",
      data: {
        flight_instance_id: flightInstanceId,
        passengers: cleanPassengers.map((p, idx) => ({
          name: p.name,
          age: p.age,
          gender: p.gender,
          id_proof_type: "ID",
          id_proof_number: `TEMP-${idx + 1}`,
          seat_no: null,
        })),
      },
      onSuccess: (res) => {
        const booking = res?.data?.booking || res?.booking || res;
        ShowToast(
          `Booking confirmed${booking?.pnr ? ` – PNR ${booking.pnr}` : ""}`,
          Severty.SUCCESS,
        );
        setBookingModalOpen(false);
      },
      onError: () => {
        ShowToast("Failed to create booking", Severty.ERROR);
      },
      finally: () => setBookingLoading(false),
    });
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Agent Booking</Title>
      <Text type="secondary">
        Search flights, review options and confirm bookings for your customers.
      </Text>

      <Card style={{ marginTop: 24 }}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            passengers: 1,
            date: dayjs(),
          }}
          onFinish={onSearch}
        >
          <Row gutter={16}>
            <Col xs={24} md={6}>
              <Form.Item
                label="From (IATA)"
                name="source"
                rules={[{ required: true, message: "Source is required" }]}
              >
                <Input placeholder="DEL" maxLength={5} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item
                label="To (IATA)"
                name="destination"
                rules={[{ required: true, message: "Destination is required" }]}
              >
                <Input placeholder="DXB" maxLength={5} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item
                label="Date"
                name="date"
                rules={[{ required: true, message: "Travel date is required" }]}
              >
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item
                label="Passengers"
                name="passengers"
                rules={[{ required: true, message: "Passengers required" }]}
              >
                <InputNumber min={1} max={9} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="end">
            <Col>
              <Space>
                <Select
                  value={sortBy}
                  onChange={(v) => setSortBy(v)}
                  style={{ width: 160 }}
                  options={[
                    { value: "price", label: "Sort: Lowest price" },
                    { value: "time", label: "Sort: Earliest depart" },
                  ]}
                />
                <Button type="primary" htmlType="submit" loading={searching}>
                  Search Flights
                </Button>
              </Space>
            </Col>
          </Row>
        </Form>
      </Card>

      <Card style={{ marginTop: 24 }} title="Search Results">
        {results.length === 0 ? (
          <Empty description="No flights found. Try another date or route." />
        ) : (
          <Row gutter={[16, 16]}>
            {results.map((flight) => {
              const airlineName = flight.airline?.name || "Airline";
              const airlineCode = flight.airline?.iata_code || "--";
              const travelDate = form.getFieldValue("date")?.format("DD MMM, YYYY");
              const pax = form.getFieldValue("passengers") || 1;
              const totalPrice = (Number(flight.price) || 0) * Number(pax || 1);
              return (
                <Col xs={24} lg={12} key={flight.flight_instance_id}>
                  <Card
                    bordered={false}
                    style={{
                      borderRadius: 16,
                      boxShadow:
                        "0 12px 30px rgba(15, 23, 42, 0.10), 0 1px 4px rgba(15, 23, 42, 0.08)",
                    }}
                    bodyStyle={{ padding: 16 }}
                  >
                    <Space
                      align="start"
                      style={{ width: "100%", justifyContent: "space-between" }}
                    >
                      <Space align="start">
                        <Avatar
                          shape="square"
                          size={44}
                          src={flight.airline?.logo_url}
                          style={{
                            background: "#eef2ff",
                            color: "#1d4ed8",
                            fontWeight: 700,
                            borderRadius: 12,
                          }}
                        >
                          {initials(airlineName)}
                        </Avatar>
                        <Space direction="vertical" size={2}>
                          <Space size={10} wrap>
                            <Text strong style={{ fontSize: 14 }}>
                              {airlineName}
                            </Text>
                            <Tag color="default">{airlineCode}</Tag>
                            <Tag color="geekblue">{flight.flight_number}</Tag>
                          </Space>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {travelDate ? `Travel date: ${travelDate}` : " "}
                          </Text>
                        </Space>
                      </Space>

                      <Space direction="vertical" align="end" size={2}>
                        <Tag color="green" style={{ marginInlineEnd: 0 }}>
                          ${flight.price} / pax
                        </Tag>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Total: <strong>${totalPrice}</strong>
                        </Text>
                      </Space>
                    </Space>

                    <Divider style={{ margin: "12px 0" }} />

                    <Row align="middle">
                      <Col span={10}>
                        <Space direction="vertical" size={0}>
                          <Text strong style={{ fontSize: 16 }}>
                            {flight.departure_time}
                          </Text>
                          <Text type="secondary">{flight.source}</Text>
                        </Space>
                      </Col>
                      <Col span={4} style={{ textAlign: "center" }}>
                        <Space direction="vertical" size={2}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {formatDuration(flight.duration_minutes)}
                          </Text>
                          <div
                            style={{
                              height: 2,
                              width: "100%",
                              background:
                                "linear-gradient(90deg, rgba(37,99,235,0.25), rgba(37,99,235,0.85), rgba(37,99,235,0.25))",
                              borderRadius: 999,
                            }}
                          />
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Direct
                          </Text>
                        </Space>
                      </Col>
                      <Col span={10} style={{ textAlign: "right" }}>
                        <Space direction="vertical" size={0}>
                          <Text strong style={{ fontSize: 16 }}>
                            {flight.arrival_time}
                          </Text>
                          <Text type="secondary">{flight.destination}</Text>
                        </Space>
                      </Col>
                    </Row>

                    <Space
                      wrap
                      size={8}
                      style={{ marginTop: 12, justifyContent: "space-between", width: "100%" }}
                    >
                      <Space wrap size={8}>
                        <Tag color="blue">
                          Availability: <strong>{flight.available_seats}</strong>
                        </Tag>
                        <Tag color="default">
                          Passengers: <strong>{pax}</strong>
                        </Tag>
                        <Tag color={flight.available_seats >= pax ? "green" : "volcano"}>
                          {flight.available_seats >= pax ? "Bookable" : "Not enough seats"}
                        </Tag>
                      </Space>
                      <Button
                        type="primary"
                        onClick={() => openBooking(flight)}
                        disabled={flight.available_seats < pax}
                      >
                        Book Now
                      </Button>
                    </Space>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Card>

      <Modal
        open={bookingModalOpen}
        onCancel={() => setBookingModalOpen(false)}
        onOk={submitBooking}
        confirmLoading={bookingLoading}
        okText="Confirm Booking"
        title="Passenger Details"
        width={720}
      >
        {selectedResult && (
          <Space direction="vertical" style={{ width: "100%" }} size={16}>
            <Card size="small" bordered={false} style={{ background: "#f9fafb" }}>
              <Space
                align="center"
                style={{ width: "100%", justifyContent: "space-between" }}
              >
                <Space>
                  <Text strong>{selectedResult.flight_number}</Text>
                  <Tag color="default">{selectedResult.airline?.name}</Tag>
                </Space>
                <Tag color="green">${selectedResult.price} / pax</Tag>
              </Space>
              <Divider style={{ margin: "10px 0" }} />
              <Row>
                <Col span={12}>
                  <Text type="secondary">
                    {selectedResult.source} → {selectedResult.destination}
                  </Text>
                </Col>
                <Col span={12} style={{ textAlign: "right" }}>
                  <Text type="secondary">
                    {selectedResult.departure_time} – {selectedResult.arrival_time} •{" "}
                    {formatDuration(selectedResult.duration_minutes)}
                  </Text>
                </Col>
              </Row>
            </Card>

            {passengers.map((p, idx) => (
              <Card
                key={idx}
                size="small"
                title={`Passenger ${idx + 1}`}
                style={{ borderRadius: 10 }}
              >
                <Row gutter={12}>
                  <Col xs={24} md={10}>
                    <Input
                      placeholder="Full name"
                      value={p.name}
                      onChange={(e) => updatePassenger(idx, "name", e.target.value)}
                    />
                  </Col>
                  <Col xs={12} md={4}>
                    <InputNumber
                      placeholder="Age"
                      min={0}
                      style={{ width: "100%" }}
                      value={p.age}
                      onChange={(value) => updatePassenger(idx, "age", value)}
                    />
                  </Col>
                  <Col xs={12} md={6}>
                    <Select
                      value={p.gender}
                      onChange={(value) => updatePassenger(idx, "gender", value)}
                      style={{ width: "100%" }}
                      options={[
                        { value: "M", label: "Male" },
                        { value: "F", label: "Female" },
                        { value: "O", label: "Other" },
                      ]}
                    />
                  </Col>
                </Row>
              </Card>
            ))}
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default AgentBooking;

